import type { ChainId } from '@morpho-org/blue-sdk'
import type { Address } from 'viem'

import type { VaultLookup } from '@/lib/procedures/vaultLookup.contract'

import { orpc } from '@/lib/orpc/client'
import { errorCodeOf } from '@/lib/orpc/errors'

import type { DistributionAsset } from './config'

export interface DistributionAssetInput {
  chainId: ChainId
  address: Address
  vaultVersion: 'v1' | 'v2'
}

export const distributionAssetQueryKey = ({
  chainId,
  address,
  vaultVersion
}: DistributionAssetInput) => ['vaultDistributionAsset', vaultVersion, chainId, address] as const

/** Null when the token's decimals are unknown, since no amount can be formatted then. */
export function toDistributionAsset(
  asset: VaultLookup['asset'],
  chainId: ChainId,
  priceUsd: number | null
): DistributionAsset | null {
  if (asset.decimals === null) return null
  return {
    address: asset.address,
    symbol: asset.symbol ?? '',
    name: asset.symbol ?? '',
    decimals: asset.decimals,
    logoURI: asset.logoUri,
    chain: { id: chainId },
    priceUsd
  }
}

const readAssetPriceUsd = async ({ vaultVersion, ...vault }: DistributionAssetInput) => {
  const state =
    vaultVersion === 'v1'
      ? await orpc.vaultV1.state.call(vault)
      : await orpc.vaultV2.state.call(vault)
  return state.asset.priceUsd
}

/**
 * The vault asset the table formats every deposit in: the token from `vault.lookup` and its USD
 * price from the vault state. A failed price read only leaves the USD values out.
 */
export async function fetchDistributionAsset(input: DistributionAssetInput) {
  const vault = { chainId: input.chainId, address: input.address }
  const [lookup, priceUsd] = await Promise.all([
    orpc.vault.lookup.call(vault).catch((error: unknown) => {
      if (errorCodeOf(error) === 'NOT_FOUND') return null
      throw error
    }),
    readAssetPriceUsd(input).catch(() => null)
  ])
  return lookup && toDistributionAsset(lookup.asset, input.chainId, priceUsd)
}
